import {DatePicker} from '@github-ui/date-picker'
import {AlertIcon} from '@primer/octicons-react'
import {Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import {type ComponentPropsWithoutRef, useCallback, useMemo, useState} from 'react'

import {formatDateString} from '../../utils/date-formatting'
import {LABELS} from './constants/labels'
import styles from './IssueFieldItem.module.css'

// Normalized hour for timezone-safe date storage (noon UTC)
export const NORMALIZED_HOUR = 12

export type IssueFieldDateEditorProps = {
  fieldId: string
  fieldName: string
  value: Date | null
  onCommit: (fieldId: string, value: Date | null) => void
  shouldOpen?: boolean
  isDraft?: boolean
  readonly: boolean
  errorPresent?: boolean
  className?: string
}

type IssueFieldDateEditorButtonProps = {
  anchorProps: ComponentPropsWithoutRef<'button'>
  fieldName: string
  value: Date | null
  displayValue: string
  formattedDate: string
  isOpen: boolean
  errorPresent: boolean
  className?: string
}

const IssueFieldDateEditorButton = ({
  anchorProps,
  fieldName,
  value,
  displayValue,
  formattedDate,
  isOpen,
  errorPresent = false,
  className,
}: IssueFieldDateEditorButtonProps) => {
  const content = (
    <button
      {...anchorProps}
      className={clsx(
        styles.issueFieldFormWrapper,
        styles.issueFieldFormWrapperClickable,
        isOpen && styles.isEditing,
        className,
      )}
      aria-label={value ? `Edit ${fieldName}` : `Set ${fieldName}`}
    >
      <span className={styles.issueFieldLabel}>{fieldName}</span>
      <span className={clsx(styles.issueFieldValueText, formattedDate && styles.hasValue)}>{displayValue}</span>
      {errorPresent && (
        <span className={styles.alertIconWrapper}>
          <AlertIcon className={styles.alertIcon} />
        </span>
      )}
    </button>
  )

  return errorPresent ? (
    <Tooltip text={LABELS.errorUpdatingIssueField} type="description">
      {content}
    </Tooltip>
  ) : (
    content
  )
}

export const IssueFieldDateEditor = ({
  fieldId,
  fieldName,
  value,
  onCommit,
  shouldOpen = false,
  isDraft = false,
  readonly,
  errorPresent = false,
  className,
}: IssueFieldDateEditorProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(shouldOpen)

  const formattedDate = useMemo(() => {
    if (!value) return ''
    return formatDateString(value)
  }, [value])

  // Convert UTC-normalized date to local date for DatePicker display
  const datePickerValue = useMemo(() => {
    if (!value) return null
    // Extract UTC date components and create local Date with same calendar date
    return new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate())
  }, [value])

  const onClose = useCallback(() => {
    if (isDraft) {
      onCommit(fieldId, null)
    }
    setIsOpen(false)
  }, [isDraft, fieldId, onCommit])

  const handleDateChange = useCallback(
    (date: Date | null) => {
      // Normalize the date to avoid timezone issues
      let normalizedDate: Date | null = null
      if (date) {
        normalizedDate = new Date(
          Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), NORMALIZED_HOUR, 0, 0, 0),
        )
      }
      onCommit(fieldId, normalizedDate)
      setIsOpen(false)
    },
    [fieldId, onCommit],
  )

  const displayValue = formattedDate || LABELS.setValue

  if (readonly) {
    return (
      <div className={clsx(styles.issueFieldFormWrapper, className)}>
        <p className={styles.issueFieldLabel}>{fieldName}</p>
        <div className={clsx(styles.issueFieldValueText, formattedDate && styles.hasValue)}>{displayValue}</div>
      </div>
    )
  }

  return (
    <DatePicker
      variant="single"
      showClearButton
      value={datePickerValue}
      onChange={handleDateChange}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={onClose}
      anchor={anchorProps => (
        <IssueFieldDateEditorButton
          anchorProps={anchorProps}
          fieldName={fieldName}
          value={value}
          displayValue={displayValue}
          formattedDate={formattedDate}
          isOpen={isOpen}
          errorPresent={errorPresent}
          className={className}
        />
      )}
    />
  )
}
